/**
 * Visitor ID Management
 *
 * Generates and persists a unique visitor ID in localStorage to track
 * anonymous users across sessions. This mirrors the server-side visitor-id
 * cookie behavior but managed client-side for stateless API endpoints.
 */

import {setWithExpiry, getWithExpiry} from "./local-storage"

const VISITOR_ID_KEY = "banzai-visitor-id"
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000 // 1 year in milliseconds

/**
 * Generates a cryptographically secure random value 0-15 for UUID generation
 * Uses crypto.getRandomValues as primary fallback before Math.random
 * @returns {number} Random value 0-15
 */
const getSecureRandom = () => {
  // Use crypto.getRandomValues() if available (more secure than Math.random)
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const array = new Uint8Array(1)
    crypto.getRandomValues(array)
    return array[0] % 16
  }

  // Final fallback to Math.random() - warn about security implications
  console.warn("Using insecure Math.random() for visitor ID generation. Consider upgrading browser.")
  return (Math.random() * 16) | 0
}

/**
 * Generates a UUID v4 compliant string using the browser's crypto API
 * Falls back to crypto.getRandomValues(), then Math.random() for older browsers
 * @returns {string} UUID in format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
const generateUUID = () => {
  // Use native crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Improved fallback with better entropy
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = getSecureRandom()
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Validates that a string doesn't contain invalid UUID characters
 * Matches the server-side validation logic
 * @param {string} uuid - The string to validate
 * @returns {boolean} True if valid UUID characters only
 */
const isValidUUID = (uuid) => {
  if (!uuid || typeof uuid !== "string") return false
  // Match server-side logic: contains only valid UUID characters (hex digits and hyphens)
  return !/[^-a-fA-F0-9]/.test(uuid)
}

/**
 * Gets the visitor ID from localStorage, or generates a new one if not found
 * @returns {string} The visitor ID (UUID format)
 */
export const getOrCreateVisitorId = () => {
  try {
    // Try to get existing visitor ID from localStorage
    const existingId = getWithExpiry(VISITOR_ID_KEY)

    // If we have a valid existing ID, return it
    if (existingId && isValidUUID(existingId)) {
      return existingId
    }

    // Generate new visitor ID
    const newId = generateUUID()

    // Store with 1-year expiry
    setWithExpiry(VISITOR_ID_KEY, newId, ONE_YEAR_MS)

    return newId
  } catch (error) {
    // If localStorage is blocked (privacy mode, etc.), generate ephemeral ID
    // This won't persist but at least provides a session-level identifier
    console.warn("[VisitorID] localStorage unavailable, using ephemeral ID:", error.message)
    return generateUUID()
  }
}
